/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useEffect,
  useState,
  ReactElement,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  Grid,
  IconButton,
  Switch,
  Typography,
  FormControlLabel,
} from '@material-ui/core';
import MUIDataTable, {
  MUIDataTableColumnDef,
  MUIDataTableOptions,
} from 'mui-datatables';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import moment from 'moment';
import clsx from 'clsx';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { useQuery, ApolloQueryResult } from '@apollo/client';
import PageError from '../../../../components/PageError';
import MUITableLoading from '../../../../components/MUITableLoading/MUITableLoading';
import useStyles from './styles';
import { GET_REMAINING_TITLES } from '../../../../queries/dashboard';
import { UserService } from '../../../../services/User/UserService';

moment.updateLocale('en', {
  week: {
    dow: 1,
  },
});

interface IPendingTitlesProps {
  setUpdatePendingTitles: Dispatch<
    SetStateAction<
      (
        variables?:
          | Partial<{
              sites: string[];
              dateStart: Date;
              dateEnd: Date;
            }>
          | undefined,
      ) => Promise<ApolloQueryResult<any>>
    >
  >;
}

export default function PendingTitles({
  setUpdatePendingTitles,
}: IPendingTitlesProps): ReactElement {
  const sites = UserService.useSitesFromAccess();
  const classes = useStyles();

  const [selectedDate, setSelectedDate] = useState(moment().toDate());
  const [onlyWithPendingTitles, setOnlyWithPendingTitles] = useState(false);
  const [remainingTitles, setRemainingTitles] = useState<IRemainingTitle[]>([]);

  const { error, data, loading, refetch } = useQuery(GET_REMAINING_TITLES, {
    variables: {
      sites,
      dateStart: moment(selectedDate).startOf('week').toDate(),
      dateEnd: moment(selectedDate).endOf('week').toDate(),
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setUpdatePendingTitles(() => refetch);
  }, [refetch]);

  useEffect(() => {
    if (data) {
      setRemainingTitles(data.remainingTitles);
    }
  }, [data]);

  const handleWeekChange = (date: MaterialUiPickersDate) => {
    setSelectedDate(moment(date).startOf('week').toDate());
  };

  const renderWrappedWeekDay = (
    date: MaterialUiPickersDate,
    selectedDate: MaterialUiPickersDate,
    dayInCurrentMonth: boolean,
  ) => {
    const dateClone = moment(date);
    const selectedDateClone = moment(selectedDate);

    const start = moment(selectedDateClone).startOf('week');
    const end = moment(selectedDateClone).endOf('week');

    const dayIsBetween = moment(dateClone).isBetween(start, end, 'day', '()');
    const isFirstDay = moment(dateClone).isSame(start, 'day');
    const isLastDay = moment(dateClone).isSame(end, 'day');

    const wrapperClassName = clsx({
      [classes.highlight]: dayIsBetween || isFirstDay || isLastDay,
      [classes.firstHighlight]: isFirstDay,
      [classes.endHighlight]: isLastDay,
    });

    const dayClassName = clsx(classes.day, {
      [classes.nonCurrentMonthDay]: !dayInCurrentMonth,
      [classes.highlightNonCurrentMonthDay]: !dayInCurrentMonth && dayIsBetween,
    });

    return (
      <div className={wrapperClassName}>
        <IconButton className={dayClassName}>
          <span>{moment(dateClone).format('D')}</span>
        </IconButton>
      </div>
    );
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOnlyWithPendingTitles(e.target.checked);
    if (e.target.checked) {
      setRemainingTitles(
        remainingTitles.filter((t) => t.ar > 0 || t.nw > 0 || t.tl > 0),
      );
    } else {
      setRemainingTitles(data?.remainingTitles || []);
    }
  };

  const formatWeekSelectLabel = (
    date: MaterialUiPickersDate,
    invalidLabel: string,
  ) => {
    const dateClone = moment(date);
    return dateClone && moment(dateClone).isValid()
      ? `Week ${moment(dateClone).week()} ( ${moment(dateClone)
          .startOf('week')
          .format('DD MMM')} - ${moment(dateClone)
          .endOf('week')
          .format('DD MMM')} )`
      : invalidLabel;
  };

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'siteDomain',
      label: 'Site',
      options: {
        filter: false,
        sort: true,
        customBodyRender: function ActionsCell(value: any) {
          return <b>{value}</b>;
        },
      },
    },
    {
      name: 'ar',
      label: 'Article (AR)',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'nw',
      label: 'News (NW)',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'tl',
      label: 'Translator (TL)',
      options: {
        filter: false,
        sort: true,
      },
    },
  ];

  const options: MUIDataTableOptions = {
    selectableRowsOnClick: false,
    selectableRowsHeader: false,
    selectableRows: 'none',
    responsive: 'standard',
    sort: true,
    filter: false,
    pagination: true,
    serverSide: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    customToolbar: undefined,
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 25, 50],
    setTableProps: () => ({
      style: { width: '98%' },
    }),
  };

  if (error) return <PageError />;

  return (
    <Grid
      container
      className={classes.grid}
      style={{
        boxShadow:
          '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
        backgroundColor: 'white',
      }}
    >
      <Grid item xs={12} className={classes.gridItem}>
        <Grid
          container
          spacing={0}
          style={{
            marginBottom: 16,
          }}
        >
          <Grid item xs={6}>
            <Typography className={classes.title} variant="h4" id="tableTitle">
              Week Pending Titles
              <Typography className={classes.subtitle}>
                {' ('}
                {moment(selectedDate).startOf('week').format('DD MMM')}
                {' - '}
                {moment(selectedDate).endOf('week').format('DD MMM')}
                {') '}
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <Grid container spacing={2} direction="row">
                <Grid item xs={6} style={{ paddingTop: 20 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={onlyWithPendingTitles}
                        onChange={handleSwitchChange}
                        name="onlyPendingTitles"
                        inputProps={{ 'aria-label': 'secondary checkbox' }}
                      />
                    }
                    label="Only sites with pending titles"
                  />
                </Grid>
                <Grid item xs={6} style={{ paddingTop: 20 }}>
                  <DatePicker
                    label="Week"
                    variant="inline"
                    value={selectedDate}
                    onChange={handleWeekChange}
                    renderDay={renderWrappedWeekDay}
                    labelFunc={formatWeekSelectLabel}
                  />
                </Grid>
              </Grid>
            </MuiPickersUtilsProvider>
          </Grid>
        </Grid>

        {loading ? (
          <MUITableLoading
            withRefresh={false}
            hasFilter={false}
            selectable="none"
            columns={['Site', 'Article (AR)', 'News (NW)', 'Translator (TL)']}
          />
        ) : (
          <MUIDataTable
            title=""
            data={remainingTitles}
            columns={columns}
            options={{
              ...options,
            }}
          />
        )}
      </Grid>
    </Grid>
  );
}
